import { getEnvironment, isEnvironment } from './environment';
import cloneDeep from 'lodash/cloneDeep';
import { getRegionShortName, getRegionSuffix } from './getRegion';
import merge from 'lodash/merge';
import { Environments } from '../types/environments.types';
import { generateWlc360iUrl } from './generateWlc360iUrl';
const generateAssetUrl = (subdomain, env, stage) => {
    const envLetter = getUrlEnvLetterFromEnv(env);
    const regionShortName = getRegionShortName();
    return `https://${subdomain}.wlc360a-${envLetter}-${regionShortName}.autodesk.com/${stage}`;
};
const generateWaterAIUrl = (subdomain, env, stage) => {
    const envLetter = getUrlEnvLetterFromEnv(env);
    const regionShortName = getRegionShortName();
    return `https://${subdomain}.wlc360ai-${envLetter}-${regionShortName}.autodesk.com/${stage}`;
};
export const generateWaterAIUrlCloudOs = (subdomain, env) => {
    const envLetter = getUrlEnvLetterFromEnv(env);
    const regionShortName = getRegionShortName();
    return `https://${subdomain}.wlc360ai-${envLetter}-${regionShortName}.cloudos.autodesk.com`;
};
export const generateStandardUrl = (subDomain, env) => {
    const region = getRegionSuffix();
    return `https://${subDomain}.info360${region ? '-' + region : ''}-${env === 'prod' ? 'prd' : env}.com/${env}`;
};
export const removePathPartFromUrl = (url) => {
    const urlParts = url.split('/');
    urlParts.pop();
    return urlParts.join('/');
};
export const generateNotificationHubAppsyncUrl = (subdomain) => {
    const region = getRegionSuffix();
    return `https://${subdomain}.info360${region ? '-' + region : ''}-prd.com/graphql`;
};
const getUrlEnvLetterFromEnv = (env) => {
    switch (env) {
        case Environments.LOCAL:
            return 'c';
        case Environments.DEV:
            return 'c';
        case Environments.QA:
            return 's';
        case Environments.STAGE:
            return 's';
        case Environments.PROD:
            return 'p';
    }
};
export const generatePlatformUrl = (subdomain, env, stage) => {
    const regionShortName = getRegionShortName();
    const envTag = getUrlEnvLetterFromEnv(env);
    //example: https://saas-notification-hub-prd-prd-appsync.saas-notification-hub.wlc360p-p-uw2.autodesk.com
    return (`https://${subdomain}.wlc360p-${envTag}-${regionShortName}.autodesk.com` + (stage ? `/${stage}` : ''));
};
export const generateTilesUrl = (subdomain, env, stage) => {
    const envLetter = getUrlEnvLetterFromEnv(env);
    const regionShortName = getRegionShortName();
    return `https://${subdomain}.wlc360p-${envLetter}-${regionShortName}.autodesk.com/${stage}/v2`;
};
export const generateDeviantUrl = (url) => {
    const regionRegex = /\|region\|/g;
    const region = getRegionSuffix();
    return region
        ? url.replace(regionRegex, '-' + region)
        : url.replace(regionRegex, '');
};
export const generateRegionForcedStandardUrl = (region, subDomain, env) => `https://${subDomain}.info360${region === 'eu' || region === 'ap' ? '-' + region : ''}-${env === 'prod' ? 'prd' : env}.com/${env}`;
export const STANDARD_LOCAL_URL = 'http://localhost:3276/dev';
/** generate feature flagged urls
 * @param featureFlags - feature flags object
 * @param ffUrls - array of tuples containing feature flag and url
 * @param fallbackUrl - fallback url if no feature flag is enabled
 * @return url string
 *
 * @example
 * const featureFlags = {
 *   releaseMapManagementCloudOs: true,
 *  // other flags...
 * };
 * const ffUrls = [
 *  ['i-360-release-map-management-cloudos', 'https://example.com'],
 * ];
 * const fallbackUrl = 'https://fallback.com';
 * const url = useFFUrl(featureFlags, ffUrls, fallbackUrl);
 */
export const useFFUrl = (featureFlags, ffUrls, fallbackUrl) => {
    for (const [ff, url] of ffUrls) {
        if (featureFlags[ff]) {
            return url;
        }
    }
    return fallbackUrl;
};
const serviceMap = (featureFlags = {}) => ({
    [Environments.LOCAL]: {
        alertsManager: generateStandardUrl('saas-alerts-management', 'dev'),
        alertsScheduler: generateStandardUrl('saas-alerts-scheduler', 'dev'),
        analyticFunctions: generateStandardUrl('saas-analytic-functions', 'dev'),
        assets: generateStandardUrl('saas-asset-management', 'dev'),
        assetsV2: generateStandardUrl('saas-asset-management', 'dev').concat('/v2'),
        billing: generateStandardUrl('saas-billing', 'dev'),
        edgeManager: generateStandardUrl('saas-edge-manager', 'dev'),
        entitlements: generateStandardUrl('saas-auth', 'dev'),
        iliEngine: generateStandardUrl('saas-ili-engine', 'dev'),
        iliManagement: generateStandardUrl('saas-ili-management', 'dev'),
        incident: generateStandardUrl('saas-incident-management', 'dev'),
        looker: generateDeviantUrl('https://saas-looker.info360|region|-dev.com/dev/looker'),
        lookerAlerts: generateStandardUrl('saas-looker-alerts', 'dev'),
        massBalance: generateStandardUrl('saas-mass-balance', 'dev'),
        rules: generateStandardUrl('saas-rule-management', 'dev'),
        sensors: generateStandardUrl('saas-sensor', 'dev'),
        simulation: generateDeviantUrl('https://saas-simulation.info360|region|-dev.com/dev/'),
        workspaces: generateStandardUrl('saas-workspace', 'dev'),
        tiles: useFFUrl(featureFlags, [
            [
                'i-360-tileset-read-from-cloudos',
                generateTilesUrl('tiles-dev', Environments.LOCAL, 'dev'),
            ],
        ], generateStandardUrl('tiles-dev', 'dev')),
        amUpload: generateStandardUrl('saas-upload-service', 'dev'),
        amInspection: generateStandardUrl('saas-inspection-management', 'dev'),
        superAdmin: generateStandardUrl('saas-superadmin', 'dev'),
        amRisk: generateStandardUrl('saas-risk-management', 'dev'),
        amRiskWss: generateDeviantUrl('wss://saas-asset-web-socket.info360|region|-dev.com/dev'),
        dataAuthorizer: generateDeviantUrl('https://saas-info360-sensor-data-dev.info360|region|-dev.com/dev'),
        sensorData: generateDeviantUrl('https://saas-info360-sensor-data-dev.info360|region|-dev.com'),
        sensorDataV2: generateDeviantUrl('https://saas-info360-sensor-data-dev.info360|region|-dev.com/dev'),
        valueMapping: generateStandardUrl('saas-value-mapping', 'dev'),
        sensorGroup: generateStandardUrl('saas-sensor-group', 'dev'),
        liftStations: generateStandardUrl('saas-lift-stations', 'dev'),
        customAnalytics: generateStandardUrl('saas-custom-analytics', 'dev'),
        patterns: useFFUrl(featureFlags, [['use-wlc360i-url-format-patterns', generateWlc360iUrl('saas-pattern', 'dev')]], generateStandardUrl('saas-pattern', 'dev')),
        amRehab: useFFUrl(featureFlags, [
            [
                'use-saas-am-rehab-backend',
                generateAssetUrl('saas-am-rehab', Environments.LOCAL, 'dev'),
            ],
        ], generateStandardUrl('saas-rehab-management', 'dev')),
        saasAmRehab: generateAssetUrl('saas-am-rehab', Environments.LOCAL, 'dev'),
        export: generateStandardUrl('saas-export-service', 'dev'),
        imageManagement: generateStandardUrl('saas-image-management', 'dev'),
        idp: generateStandardUrl('saas-idp-router', 'dev'),
        serviceIntegration: generateStandardUrl('saas-serviceintegration', 'dev'),
        mapManagement: useFFUrl(featureFlags, [
            [
                'i-360-release-map-management-cloudos',
                generatePlatformUrl('saas-map-management', Environments.LOCAL, 'dev'),
            ],
        ], generateStandardUrl('saas-map-management', 'dev')),
        emaginOptimization: generateStandardUrl('saas-emagin-optimization-api', 'dev'),
        emaginHarvester: removePathPartFromUrl(generateStandardUrl('saas-emagin-harvester', 'dev')),
        emaginMoments: generateStandardUrl('saas-emagin-moments', 'dev'),
        sharedTemplates: generateStandardUrl('saas-shared-templates', 'dev'),
        sharedTemplatesProxy: generateStandardUrl('saas-shared-templates', 'dev'),
        emaginIntegeration: generateStandardUrl('saas-info360-emagin-integ', 'dev'),
        favorites: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-favorites',
                generateWlc360iUrl('saas-favorites', 'dev'),
            ],
        ], generateStandardUrl('saas-favorites', 'dev')),
        userManagement: generateStandardUrl('saas-user-management', 'dev'),
        forgeApi: 'https://api.stg-manage.autodesk.com',
        notificationHub: 'https://saas-notification-hub-appsync-dev-dev.info360-dev.com/graphql',
        notificationHubPlatform: 'https://saas-notification-hub-dev-dev-appsync.saas-notification-hub.wlc360p-c-uw2.autodesk.com/graphql',
        edgeAnalytics: generateStandardUrl('saas-edge-analytics', 'dev'),
        goals: generateStandardUrl('saas-goal-management', 'dev'),
        flowGPT: generateDeviantUrl('https://saas-emagin-flowgpt.info360|region|-dev.com'),
        semantic: generateStandardUrl('saas-emagin-semantic', 'dev'),
        optimizationApi: generateStandardUrl('saas-emagin-optimization-api', 'dev'),
        modelFactory: generateStandardUrl('saas-emagin-model-factory', 'dev'),
        workgroupDatastore: generateStandardUrl('saas-workgroup-datastore', 'dev'),
        simulationResults: generateStandardUrl('saas-simulation-results-dev', 'dev'),
        saasPipeDeterioration: generateWaterAIUrl('saas-water-ai-pipe-deterioration', Environments.LOCAL, 'dev'),
        saasAmCost: generateAssetUrl('saas-am-cost', Environments.LOCAL, 'dev'),
        w3fAgentServer: generateWaterAIUrlCloudOs('w3f-agent-server', Environments.LOCAL),
        amso: generateWaterAIUrlCloudOs('amso', Environments.LOCAL),
    },
    [Environments.DEV]: {
        alertsManager: generateStandardUrl('saas-alerts-management', 'dev'),
        alertsScheduler: generateStandardUrl('saas-alerts-scheduler', 'dev'),
        analyticFunctions: generateStandardUrl('saas-analytic-functions', 'dev'),
        assets: generateStandardUrl('saas-asset-management', 'dev'),
        assetsV2: generateStandardUrl('saas-asset-management', 'dev').concat('/v2'),
        billing: generateStandardUrl('saas-billing', 'dev'),
        edgeManager: generateStandardUrl('saas-edge-manager', 'dev'),
        entitlements: generateStandardUrl('saas-auth', 'dev'),
        iliEngine: generateStandardUrl('saas-ili-engine', 'dev'),
        iliManagement: generateStandardUrl('saas-ili-management', 'dev'),
        incident: generateStandardUrl('saas-incident-management', 'dev'),
        looker: generateDeviantUrl('https://saas-looker.info360|region|-dev.com/dev/looker'),
        lookerAlerts: generateStandardUrl('saas-looker-alerts', 'dev'),
        massBalance: generateStandardUrl('saas-mass-balance', 'dev'),
        rules: generateStandardUrl('saas-rule-management', 'dev'),
        sensors: generateStandardUrl('saas-sensor', 'dev'),
        simulation: generateDeviantUrl('https://saas-simulation.info360|region|-dev.com/dev/'),
        workspaces: generateStandardUrl('saas-workspace', 'dev'),
        tiles: useFFUrl(featureFlags, [
            [
                'i-360-tileset-read-from-cloudos',
                generateTilesUrl('tiles-dev', Environments.DEV, 'dev'),
            ],
        ], generateStandardUrl('tiles-dev', 'dev')),
        amUpload: generateStandardUrl('saas-upload-service', 'dev'),
        amInspection: generateStandardUrl('saas-inspection-management', 'dev'),
        superAdmin: generateStandardUrl('saas-superadmin', 'dev'),
        amRisk: generateStandardUrl('saas-risk-management', 'dev'),
        amRiskWss: generateDeviantUrl('wss://saas-asset-web-socket.info360|region|-dev.com/dev'),
        dataAuthorizer: generateDeviantUrl('https://saas-info360-sensor-data-dev.info360|region|-dev.com/dev'),
        sensorData: generateDeviantUrl('https://saas-info360-sensor-data-dev.info360|region|-dev.com'),
        sensorDataV2: generateDeviantUrl('https://saas-info360-sensor-data-dev.info360|region|-dev.com/dev'),
        valueMapping: generateStandardUrl('saas-value-mapping', 'dev'),
        sensorGroup: generateStandardUrl('saas-sensor-group', 'dev'),
        liftStations: generateStandardUrl('saas-lift-stations', 'dev'),
        customAnalytics: generateStandardUrl('saas-custom-analytics', 'dev'),
        patterns: useFFUrl(featureFlags, [['use-wlc360i-url-format-patterns', generateWlc360iUrl('saas-pattern', 'dev')]], generateStandardUrl('saas-pattern', 'dev')),
        amRehab: useFFUrl(featureFlags, [
            [
                'use-saas-am-rehab-backend',
                generateAssetUrl('saas-am-rehab', Environments.DEV, 'dev'),
            ],
        ], generateStandardUrl('saas-rehab-management', 'dev')),
        saasAmRehab: generateAssetUrl('saas-am-rehab', Environments.DEV, 'dev'),
        export: generateStandardUrl('saas-export-service', 'dev'),
        imageManagement: generateStandardUrl('saas-image-management', 'dev'),
        idp: generateStandardUrl('saas-idp-router', 'dev'),
        serviceIntegration: generateStandardUrl('saas-serviceintegration', 'dev'),
        mapManagement: useFFUrl(featureFlags, [
            [
                'i-360-release-map-management-cloudos',
                generatePlatformUrl('saas-map-management', Environments.DEV, 'dev'),
            ],
        ], generateStandardUrl('saas-map-management', 'dev')),
        emaginOptimization: generateStandardUrl('saas-emagin-optimization-api', 'dev'),
        emaginHarvester: removePathPartFromUrl(generateStandardUrl('saas-emagin-harvester', 'dev')),
        emaginMoments: generateStandardUrl('saas-emagin-moments', 'dev'),
        sharedTemplates: generateStandardUrl('saas-shared-templates', 'dev'),
        sharedTemplatesProxy: generateStandardUrl('saas-shared-templates', 'dev'),
        emaginIntegeration: generateStandardUrl('saas-info360-emagin-integ', 'dev'),
        favorites: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-favorites',
                generateWlc360iUrl('saas-favorites', 'dev'),
            ],
        ], generateStandardUrl('saas-favorites', 'dev')),
        userManagement: generateStandardUrl('saas-user-management', 'dev'),
        forgeApi: 'https://api.stg-manage.autodesk.com',
        notificationHub: 'https://saas-notification-hub-appsync-dev-dev.info360-dev.com/graphql',
        notificationHubPlatform: 'https://saas-notification-hub-dev-dev-appsync.saas-notification-hub.wlc360p-c-uw2.autodesk.com/graphql',
        edgeAnalytics: generateStandardUrl('saas-edge-analytics', 'dev'),
        goals: generateStandardUrl('saas-goal-management', 'dev'),
        flowGPT: generateDeviantUrl('https://saas-emagin-flowgpt.info360|region|-dev.com'),
        semantic: generateStandardUrl('saas-emagin-semantic', 'dev'),
        optimizationApi: generateStandardUrl('saas-emagin-optimization-api', 'dev'),
        modelFactory: generateStandardUrl('saas-emagin-model-factory', 'dev'),
        workgroupDatastore: generateStandardUrl('saas-workgroup-datastore', 'dev'),
        simulationResults: generateStandardUrl('saas-simulation-results-dev', 'dev'),
        saasPipeDeterioration: generateWaterAIUrl('saas-water-ai-pipe-deterioration', Environments.DEV, 'dev'),
        saasAmCost: generateAssetUrl('saas-am-cost', Environments.DEV, 'dev'),
        w3fAgentServer: generateWaterAIUrlCloudOs('w3f-agent-server', Environments.DEV),
        amso: generateWaterAIUrlCloudOs('amso', Environments.DEV),
    },
    [Environments.QA]: {
        alertsManager: generateStandardUrl('saas-alerts-management', 'qa'),
        alertsScheduler: generateStandardUrl('saas-alerts-scheduler', 'qa'),
        analyticFunctions: generateStandardUrl('saas-analytic-functions', 'qa'),
        assets: generateStandardUrl('saas-asset-management', 'qa'),
        assetsV2: generateStandardUrl('saas-asset-management', 'qa').concat('/v2'),
        billing: generateStandardUrl('saas-billing', 'qa'),
        edgeManager: generateStandardUrl('saas-edge-manager', 'qa'),
        entitlements: generateStandardUrl('saas-auth', 'qa'),
        iliEngine: generateStandardUrl('saas-ili-engine', 'qa'),
        iliManagement: generateStandardUrl('saas-ili-management', 'qa'),
        incident: generateStandardUrl('saas-incident-management', 'qa'),
        looker: generateDeviantUrl('https://saas-looker.info360|region|-qa.com/qa/looker'),
        lookerAlerts: generateStandardUrl('saas-looker-alerts', 'qa'),
        massBalance: generateStandardUrl('saas-mass-balance', 'qa'),
        rules: generateStandardUrl('saas-rule-management', 'qa'),
        sensors: generateStandardUrl('saas-sensor', 'qa'),
        simulation: generateDeviantUrl('https://saas-simulation.info360|region|-qa.com/qa/'),
        workspaces: generateStandardUrl('saas-workspace', 'qa'),
        tiles: useFFUrl(featureFlags, [
            [
                'i-360-tileset-read-from-cloudos',
                generateTilesUrl('tiles-dev', Environments.QA, 'stg'),
            ],
        ], generateStandardUrl('tiles', 'qa')),
        amUpload: generateStandardUrl('saas-upload-service', 'qa'),
        amInspection: generateStandardUrl('saas-inspection-management', 'qa'),
        superAdmin: generateStandardUrl('saas-superadmin', 'qa'),
        amRisk: generateStandardUrl('saas-risk-management', 'qa'),
        amRiskWss: generateDeviantUrl('wss://saas-asset-web-socket.info360|region|-qa.com/qa'),
        dataAuthorizer: generateDeviantUrl('https://saas-info360-sensor-data-qa.info360|region|-qa.com/qa'),
        sensorData: generateDeviantUrl('https://saas-info360-sensor-data-qa.info360|region|-qa.com'),
        sensorDataV2: generateDeviantUrl('https://saas-info360-sensor-data-qa.info360|region|-qa.com/qa'),
        valueMapping: generateStandardUrl('saas-value-mapping', 'qa'),
        sensorGroup: generateStandardUrl('saas-sensor-group', 'qa'),
        liftStations: generateStandardUrl('saas-lift-stations', 'qa'),
        customAnalytics: generateStandardUrl('saas-custom-analytics', 'qa'),
        // autodeskUser: generateStandardUrl('saas-auth', 'qa'),
        patterns: useFFUrl(featureFlags, [['use-wlc360i-url-format-patterns', generateWlc360iUrl('saas-pattern', 'stg')]], generateStandardUrl('saas-pattern', 'qa')),
        amRehab: useFFUrl(featureFlags, [
            [
                'use-saas-am-rehab-backend',
                generateAssetUrl('saas-am-rehab', Environments.QA, 'stg'),
            ],
        ], generateStandardUrl('saas-rehab-management', 'qa')),
        saasAmRehab: generateAssetUrl('saas-am-rehab', Environments.QA, 'stg'),
        export: generateStandardUrl('saas-export-service', 'qa'),
        imageManagement: generateStandardUrl('saas-image-management', 'qa'),
        idp: generateStandardUrl('saas-idp-router', 'qa'),
        serviceIntegration: generateStandardUrl('saas-serviceintegration', 'qa'),
        mapManagement: useFFUrl(featureFlags, [
            [
                'i-360-release-map-management-cloudos',
                generatePlatformUrl('saas-map-management', Environments.QA, 'stg'),
            ],
        ], generateStandardUrl('saas-map-management', 'qa')),
        emaginOptimization: generateStandardUrl('saas-emagin-optimization-api', 'qa'),
        emaginHarvester: removePathPartFromUrl(generateStandardUrl('saas-emagin-harvester', 'qa')),
        emaginMoments: generateStandardUrl('saas-emagin-moments', 'qa'),
        sharedTemplates: generateStandardUrl('saas-shared-templates', 'qa'),
        sharedTemplatesProxy: generateStandardUrl('saas-shared-templates', 'qa'),
        emaginIntegeration: generateStandardUrl('saas-info360-emagin-integ', 'qa'),
        favorites: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-favorites',
                generateWlc360iUrl('saas-favorites', 'qa'),
            ],
        ], generateStandardUrl('saas-favorites', 'qa')),
        userManagement: generateStandardUrl('saas-user-management', 'qa'),
        forgeApi: 'https://api.stg-manage.autodesk.com',
        notificationHub: 'https://saas-notification-hub-appsync-qa-qa.info360-qa.com/graphql',
        notificationHubPlatform: 'https://saas-notification-hub-stg-stg-appsync.saas-notification-hub.wlc360p-s-uw2.autodesk.com/graphql',
        edgeAnalytics: generateStandardUrl('saas-edge-analytics', 'qa'),
        goals: generateStandardUrl('saas-goal-management', 'qa'),
        flowGPT: generateDeviantUrl('https://saas-emagin-flowgpt.info360|region|-qa.com'),
        semantic: generateStandardUrl('saas-emagin-semantic', 'qa'),
        optimizationApi: generateStandardUrl('saas-emagin-optimization-api', 'qa'),
        modelFactory: generateStandardUrl('saas-emagin-model-factory', 'qa'),
        workgroupDatastore: generateStandardUrl('saas-workgroup-datastore', 'qa'),
        simulationResults: generateStandardUrl('saas-simulation-results', 'qa'),
        saasPipeDeterioration: generateWaterAIUrl('saas-water-ai-pipe-deterioration', Environments.QA, 'stg'),
        saasAmCost: generateAssetUrl('saas-am-cost', Environments.QA, 'stg'),
        w3fAgentServer: generateWaterAIUrlCloudOs('w3f-agent-server', Environments.QA),
        amso: generateWaterAIUrlCloudOs('amso', Environments.QA),
    },
    [Environments.STAGE]: {
        alertsManager: generateStandardUrl('saas-alerts-management', 'qa'),
        alertsScheduler: generateStandardUrl('saas-alerts-scheduler', 'qa'),
        analyticFunctions: generateStandardUrl('saas-analytic-functions', 'qa'),
        assets: generateStandardUrl('saas-asset-management', 'qa'),
        assetsV2: generateStandardUrl('saas-asset-management', 'qa').concat('/v2'),
        billing: generateStandardUrl('saas-billing', 'qa'),
        edgeManager: generateStandardUrl('saas-edge-manager', 'qa'),
        entitlements: generateStandardUrl('saas-auth', 'qa'),
        iliEngine: generateStandardUrl('saas-ili-engine', 'qa'),
        iliManagement: generateStandardUrl('saas-ili-management', 'qa'),
        incident: generateStandardUrl('saas-incident-management', 'qa'),
        looker: generateDeviantUrl('https://saas-looker.info360|region|-qa.com/qa/looker'),
        lookerAlerts: generateStandardUrl('saas-looker-alerts', 'qa'),
        massBalance: generateStandardUrl('saas-mass-balance', 'qa'),
        rules: generateStandardUrl('saas-rule-management', 'qa'),
        sensors: generateStandardUrl('saas-sensor', 'qa'),
        simulation: generateDeviantUrl('https://saas-simulation.info360|region|-qa.com/qa/'),
        workspaces: generateStandardUrl('saas-workspace', 'qa'),
        tiles: useFFUrl(featureFlags, [
            [
                'i-360-tileset-read-from-cloudos',
                generateTilesUrl('tiles', Environments.STAGE, 'stg'),
            ],
        ], generateStandardUrl('tiles', 'qa')),
        amUpload: generateStandardUrl('saas-upload-service', 'qa'),
        amInspection: generateStandardUrl('saas-inspection-management', 'qa'),
        superAdmin: generateStandardUrl('saas-superadmin', 'qa'),
        amRisk: generateStandardUrl('saas-risk-management', 'qa'),
        amRiskWss: generateDeviantUrl('wss://saas-asset-web-socket.info360|region|-qa.com/qa'),
        dataAuthorizer: generateDeviantUrl('https://saas-info360-sensor-data-qa.info360|region|-qa.com/qa'),
        sensorData: generateDeviantUrl('https://saas-info360-sensor-data-qa.info360|region|-qa.com'),
        sensorDataV2: generateDeviantUrl('https://saas-info360-sensor-data-qa.info360|region|-qa.com/qa'),
        valueMapping: generateStandardUrl('saas-value-mapping', 'qa'),
        sensorGroup: generateStandardUrl('saas-sensor-group', 'qa'),
        liftStations: generateStandardUrl('saas-lift-stations', 'qa'),
        customAnalytics: generateStandardUrl('saas-custom-analytics', 'qa'),
        // autodeskUser: generateStandardUrl('saas-auth', 'qa'),
        patterns: useFFUrl(featureFlags, [['use-wlc360i-url-format-patterns', generateWlc360iUrl('saas-pattern', 'stg')]], generateStandardUrl('saas-pattern', 'qa')),
        amRehab: useFFUrl(featureFlags, [
            [
                'use-saas-am-rehab-backend',
                generateAssetUrl('saas-am-rehab', Environments.STAGE, 'stg'),
            ],
        ], generateStandardUrl('saas-rehab-management', 'qa')),
        saasAmRehab: generateAssetUrl('saas-am-rehab', Environments.STAGE, 'stg'),
        export: generateStandardUrl('saas-export-service', 'qa'),
        imageManagement: generateStandardUrl('saas-image-management', 'qa'),
        idp: generateStandardUrl('saas-idp-router', 'qa'),
        serviceIntegration: generateStandardUrl('saas-serviceintegration', 'qa'),
        mapManagement: useFFUrl(featureFlags, [
            [
                'i-360-release-map-management-cloudos',
                generatePlatformUrl('saas-map-management', Environments.STAGE, 'stg'),
            ],
        ], generateStandardUrl('saas-map-management', 'qa')),
        emaginOptimization: generateStandardUrl('saas-emagin-optimization-api', 'qa'),
        emaginHarvester: removePathPartFromUrl(generateStandardUrl('saas-emagin-harvester', 'qa')),
        emaginMoments: generateStandardUrl('saas-emagin-moments', 'qa'),
        sharedTemplates: generateStandardUrl('saas-shared-templates', 'qa'),
        sharedTemplatesProxy: generateStandardUrl('saas-shared-templates', 'qa'),
        emaginIntegeration: generateStandardUrl('saas-info360-emagin-integ', 'qa'),
        favorites: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-favorites',
                generateWlc360iUrl('saas-favorites', 'qa'),
            ],
        ], generateStandardUrl('saas-favorites', 'qa')),
        userManagement: generateStandardUrl('saas-user-management', 'qa'),
        forgeApi: 'https://api.stg-manage.autodesk.com',
        notificationHub: 'https://saas-notification-hub-appsync-qa-qa.info360-qa.com/graphql',
        notificationHubPlatform: 'https://saas-notification-hub-stg-stg-appsync.saas-notification-hub.wlc360p-s-uw2.autodesk.com/graphql',
        edgeAnalytics: generateStandardUrl('saas-edge-analytics', 'qa'),
        goals: generateStandardUrl('saas-goal-management', 'qa'),
        flowGPT: generateDeviantUrl('https://saas-emagin-flowgpt.info360|region|-qa.com'),
        semantic: generateStandardUrl('saas-emagin-semantic', 'qa'),
        optimizationApi: generateStandardUrl('saas-emagin-optimization-api', 'qa'),
        modelFactory: generateStandardUrl('saas-emagin-model-factory', 'qa'),
        workgroupDatastore: generateStandardUrl('saas-workgroup-datastore', 'qa'),
        simulationResults: generateStandardUrl('saas-simulation-results', 'qa'),
        saasPipeDeterioration: generateWaterAIUrl('saas-water-ai-pipe-deterioration', Environments.QA, 'stg'),
        saasAmCost: generateAssetUrl('saas-am-cost', Environments.STAGE, 'stg'),
        w3fAgentServer: generateWaterAIUrlCloudOs('w3f-agent-server', Environments.STAGE),
        amso: generateWaterAIUrlCloudOs('amso', Environments.STAGE),
    },
    [Environments.PROD]: {
        alertsManager: generateStandardUrl('saas-alerts-management', 'prod'),
        alertsScheduler: generateStandardUrl('saas-alerts-scheduler', 'prod'),
        analyticFunctions: 'https://saas-analytic-functions.info360-prd.com/prod',
        assets: generateStandardUrl('saas-asset-management', 'prod'),
        assetsV2: generateStandardUrl('saas-asset-management', 'prod').concat('/v2'),
        billing: generateStandardUrl('saas-billing', 'prod'),
        edgeManager: generateStandardUrl('saas-edge-manager', 'prod'),
        entitlements: generateStandardUrl('saas-auth', 'prod'),
        iliEngine: generateStandardUrl('saas-ili-engine', 'prod'),
        iliManagement: generateStandardUrl('saas-ili-management', 'prod'),
        incident: generateStandardUrl('saas-incident-management', 'prod'),
        looker: generateDeviantUrl('https://saas-looker.info360|region|-prd.com/prod/looker'),
        lookerAlerts: generateStandardUrl('saas-looker-alerts', 'prod'),
        massBalance: generateStandardUrl('saas-mass-balance', 'prod'),
        rules: generateStandardUrl('saas-rule-management', 'prod'),
        sensors: generateStandardUrl('saas-sensor', 'prod'),
        simulation: generateDeviantUrl('https://saas-simulation.info360|region|-prd.com/prod/'),
        workspaces: generateStandardUrl('saas-workspace', 'prod'),
        tiles: useFFUrl(featureFlags, [
            [
                'i-360-tileset-read-from-cloudos',
                generateTilesUrl('tiles', Environments.PROD, 'prd'),
            ],
        ], generateStandardUrl('tiles', 'prod')),
        amUpload: generateStandardUrl('saas-upload-service', 'prod'),
        amInspection: generateStandardUrl('saas-inspection-management', 'prod'),
        superAdmin: generateStandardUrl('saas-superadmin', 'prod'),
        amRisk: generateStandardUrl('saas-risk-management', 'prod'),
        amRiskWss: generateDeviantUrl('wss://saas-asset-web-socket.info360|region|-prd.com/prod'),
        dataAuthorizer: generateDeviantUrl('https://saas-info360-sensor-data.info360|region|-prd.com/prod'),
        sensorData: generateDeviantUrl('https://saas-info360-sensor-data.info360|region|-prd.com'),
        sensorDataV2: generateDeviantUrl('https://saas-info360-sensor-data.info360|region|-prd.com/prod'),
        valueMapping: generateStandardUrl('saas-value-mapping', 'prod'),
        sensorGroup: generateStandardUrl('saas-sensor-group', 'prod'),
        liftStations: generateStandardUrl('saas-lift-stations', 'prod'),
        customAnalytics: generateStandardUrl('saas-custom-analytics', 'prod'),
        // autodeskUser: generateStandardUrl('saas-auth', 'prod'),
        patterns: useFFUrl(featureFlags, [['use-wlc360i-url-format-patterns', generateWlc360iUrl('saas-pattern', 'prd')]], generateStandardUrl('saas-pattern', 'prod')),
        amRehab: useFFUrl(featureFlags, [
            [
                'use-saas-am-rehab-backend',
                generateAssetUrl('saas-am-rehab', Environments.PROD, 'prd'),
            ],
        ], generateStandardUrl('saas-rehab-management', 'prod')),
        saasAmRehab: generateAssetUrl('saas-am-rehab', Environments.PROD, 'prd'),
        export: generateStandardUrl('saas-export-service', 'prod'),
        imageManagement: generateStandardUrl('saas-image-management', 'prod'),
        idp: generateStandardUrl('saas-idp-router', 'prod'),
        serviceIntegration: generateStandardUrl('saas-serviceintegration', 'prod'),
        mapManagement: useFFUrl(featureFlags, [
            [
                'i-360-release-map-management-cloudos',
                generatePlatformUrl('saas-map-management', Environments.PROD, 'prd'),
            ],
        ], generateStandardUrl('saas-map-management', 'prod')),
        emaginOptimization: generateStandardUrl('saas-emagin-optimization-api', 'prod'),
        emaginHarvester: removePathPartFromUrl(generateStandardUrl('saas-emagin-harvester', 'prod')),
        emaginMoments: generateStandardUrl('saas-emagin-moments', 'prod'),
        sharedTemplates: generateStandardUrl('saas-shared-templates', 'prod'),
        sharedTemplatesProxy: generateStandardUrl('saas-shared-templates', 'prd'),
        emaginIntegeration: generateStandardUrl('saas-info360-emagin-integ', 'prod'),
        favorites: useFFUrl(featureFlags, [
            [
                'use-wlc360i-url-format-favorites',
                generateWlc360iUrl('saas-favorites', 'prod'),
            ],
        ], generateStandardUrl('saas-favorites', 'prod')),
        userManagement: generateStandardUrl('saas-user-management', 'prod'),
        forgeApi: 'https://api.manage.autodesk.com',
        notificationHub: generateNotificationHubAppsyncUrl('saas-notification-hub-appsync-prod-prod'),
        notificationHubPlatform: generatePlatformUrl('saas-notification-hub-prd-prd-appsync.saas-notification-hub', Environments.PROD) + '/graphql',
        edgeAnalytics: generateStandardUrl('saas-edge-analytics', 'prod'),
        goals: generateStandardUrl('saas-goal-management', 'prod'),
        flowGPT: generateDeviantUrl('https://saas-emagin-flowgpt.info360|region|-prd.com'),
        semantic: generateDeviantUrl('https://saas-emagin-semantic.info360|region|-prd.com/prod'),
        optimizationApi: generateDeviantUrl('https://saas-emagin-optimization-api.info360|region|-prd.com/prod'),
        modelFactory: generateDeviantUrl('https://saas-emagin-model-factory.info360|region|-prd.com/prod'),
        workgroupDatastore: generateDeviantUrl('https://saas-workgroup-datastore.info360|region|-prd.com/prod'),
        simulationResults: generateDeviantUrl('https://saas-simulation-results.info360|region|-prd.com/prod'),
        saasPipeDeterioration: generateWaterAIUrl('saas-water-ai-pipe-deterioration', Environments.PROD, 'prd'),
        saasAmCost: generateAssetUrl('saas-am-cost', Environments.PROD, 'prd'),
        w3fAgentServer: generateWaterAIUrlCloudOs('w3f-agent-server', Environments.PROD),
        amso: generateWaterAIUrlCloudOs('amso', Environments.PROD),
    },
});
export const getService = (service, mapOverride = {}, environmentOverride, featureFlags) => {
    const serviceMapClone = cloneDeep(serviceMap(featureFlags));
    const map = merge(serviceMapClone, mapOverride);
    const env = isEnvironment(environmentOverride)
        ? environmentOverride
        : getEnvironment();
    return map[env][service];
};
